import React, { useState, useEffect, useRef } from 'react';
import ZipForm from '../zip-form';
import {
  GeolocationType,
  COOKIE_NAMES,
  secureStorage
} from '../../helpers/global-storage';
import { MASTHEAD_ZIP_FORM_EVENTS } from './helpers';

import { getStoredGeolocationData } from '../../helpers/geolocation';

type MastheadZipFormProps = {
  zipInvalidMessage: string;
  zipRegex: string;
  zipPlaceholders: string;
  submitLabel: string;
  defaultCountryValue: string;
  redirectUrl: string;
  commercialUrl: string;
};

function MastheadZipForm({
  zipInvalidMessage,
  zipRegex,
  zipPlaceholders,
  submitLabel,
  defaultCountryValue,
  redirectUrl,
  commercialUrl
}: MastheadZipFormProps) {
  const regex = JSON.parse(zipRegex);
  const placeholders = JSON.parse(zipPlaceholders);

  const [ready, setReady] = useState(false);
  const [locationData, setLocationData] = useState<GeolocationType | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const fillLocationData = async () => {
    const location = getStoredGeolocationData();

    if (location) {
      setLocationData(location);
    }
  };

  const getNextStep = () => {
    let nextStep = null;
    let currentStep = null;

    if (containerRef.current) {
      const parent = containerRef.current.closest('.tabbed-container__panel');

      if (parent) {
        currentStep = parent.querySelector(
          '.tabbed-container__panel-content--gated'
        ) as HTMLElement;

        nextStep = parent.querySelector(
          '.tabbed-container__panel-content--ungated'
        ) as HTMLElement;
      }
    }

    return { currentStep, nextStep };
  };

  useEffect(() => {
    const onReady = () => setReady(true);
    window.addEventListener(MASTHEAD_ZIP_FORM_EVENTS.ready, onReady);
    window.addEventListener('commercialChange', e =>
      handleRedirectCommercial(e as CustomEvent)
    );

    return () => {
      window.removeEventListener(MASTHEAD_ZIP_FORM_EVENTS.ready, onReady);
      window.removeEventListener('commercialChange', e =>
        handleRedirectCommercial(e as CustomEvent)
      );
    };
  }, []);

  useEffect(() => {
    fillLocationData();
    const { currentStep, nextStep } = getNextStep();

    if (currentStep && nextStep) {
      if (ready) {
        currentStep.classList.add('tabbed-container__panel-content--hidden');
        nextStep.classList.remove('tabbed-container__panel-content--hidden');
      } else {
        currentStep.classList.remove('tabbed-container__panel-content--hidden');
        nextStep.classList.add('tabbed-container__panel-content--hidden');
      }
    }
  }, [ready]);

  const handleRedirectCommercial = (e: CustomEvent) => {
    if (e.detail && !window.location.href.includes(commercialUrl)) {
      window.location.href = commercialUrl;
    } else if (!e.detail && !window.location.href.includes(redirectUrl)) {
      window.location.href = redirectUrl;
    }
  };

  const handleSubmit = (zip: string, country: string) => {
    if (zip && country) {
      const location = {
        address: '',
        zip,
        country,
        latitude: '',
        longitude: ''
      };

      secureStorage.set(COOKIE_NAMES.geolocation, JSON.stringify(location));

      window.location.href = `${window.location.origin}${redirectUrl}?postalCode=${zip}&countryCode=${country}&user=true`;
      setReady(true);
      window.dispatchEvent(new CustomEvent('zipFormSubmit'));
    } else {
      setReady(false);
    }
  };

  return (
    <div className="masthead-zip-form__wrapper" ref={containerRef}>
      {!ready && (
        <ZipForm
          defaultCountryValue={
            locationData?.country
              ? locationData.country.toLowerCase()
              : defaultCountryValue.toLowerCase()
          }
          defaultZipValue={locationData?.zip ? locationData.zip : ''}
          invalidZipMessage={zipInvalidMessage}
          zipRegex={regex}
          zipPlaceholders={placeholders}
          onSubmit={handleSubmit}
          submitLabel={submitLabel}
          withSubmitButton={true}
        />
      )}
    </div>
  );
}

export default MastheadZipForm;
